import React, { useRef, useCallback } from 'react';
import { Button } from './Button.tsx';

interface FileUploadButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
    accept?: string;
    multiple?: boolean;
    onChange: (files: FileList | null) => void;
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

const NativeInput = 'input';

/**
 * FileUploadButton - Button that triggers a hidden file input
 * 
 * @example
 * <FileUploadButton
 *   accept="image/*"
 *   onChange={(files) => handleUpload(files?.[0])}
 *   variant="secondary"
 * >
 *   <Icon name="upload" />
 *   Upload Image
 * </FileUploadButton>
 */
export const FileUploadButton: React.FC<FileUploadButtonProps> = ({
    accept,
    multiple = false,
    onChange,
    variant = 'secondary',
    size = 'md',
    children,
    ...buttonProps
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.files);
        // Reset input so same file can be selected again
        e.target.value = '';
    }, [onChange]);

    return (
        <>
            <NativeInput
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileChange}
                className="hidden"
                aria-hidden="true"
            />
            <Button
                variant={variant}
                size={size}
                onClick={handleClick}
                {...buttonProps}
            >
                {children}
            </Button>
        </>
    );
};

FileUploadButton.displayName = 'FileUploadButton';














